import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { Box } from '../../components/Box'
import { Button } from '../../components/Button'
import { Flex } from '../../components/Flex'
import { Text, Title } from '../../components/Text'
import { useDateFormat } from '../../hooks/useDateFormat'
import { ContractStatus, UserProfileData } from '../../redux/account/user'
import { getCmsContactsUrl } from '../../redux/config'
import { ContractType } from '../../redux/user/types'
import { getSubscriptionDesc } from '../../utils/translations'
import { PrepaidContractInfo } from '../../components/Contract'

const ContentRow = styled.div`
  display: flex;
  flex-direction: row;

  border-bottom: 1px solid ${({ theme }) => theme.colors.borderPrimary};
  padding-bottom: 24px;
  margin-bottom: 24px;

  flex-wrap: wrap;
`

const Wrapper = styled.div`
  display: flex;
  border-radius: 4px;
  flex-direction: column;
  padding: 24px;
  width: 100%;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bgPrimary};

  ${ContentRow}:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
    margin-bottom: 0;
  }
`

export const SubscriptionInfoBox = ({ contracts }: { contracts: UserProfileData['accounting']['contracts'] }) => {
  const primaryContract = contracts === undefined ? undefined : contracts[0]

  const { t } = useTranslation(['account', 'core', 'checkout'])
  const { formatDate } = useDateFormat()
  const contactsUrl = useSelector(getCmsContactsUrl)
  const ta = (key: string) => t(`account:Accounting.${key}`)

  return (
    <Wrapper>
      <ContentRow>
        <Box width={[1, 3 / 5, 3 / 4]} pr={[0, 2, 2]} pb={[1, 0, 0]}>
          <Title level="small" bold>
            {ta('Subscription')}
          </Title>
          <Text block mt={1} mb={1}>
            {primaryContract?.status === ContractStatus.Active ? ta('Contract.Active') : ta('Contract.Inactive')}
          </Text>
          {!_.isEmpty(primaryContract?.validTo) && (
            <Text block>
              {ta('ValidUntil')}:
              <span style={{ marginInlineStart: '8px' }}>
                {formatDate(primaryContract?.validTo, {
                  dateStyle: 'medium'
                })}
              </span>
            </Text>
          )}
          <Text block mt={2}>
            {getSubscriptionDesc(contracts, t)}
          </Text>
        </Box>
        <Flex alignItems="baseline" width="100%" flexWrap="wrap" justifyContent="flex-start" pt={3} pb={3}>
          <Text pt={1} pb={1} pr={2}>
            {t('checkout:ContactUsText')}
          </Text>
          <Box pl={2}>
            <a href={contactsUrl || ''}>
              <Button variant="primary">{t('checkout:GoToContacts')}</Button>
            </a>
          </Box>
        </Flex>
      </ContentRow>
      {primaryContract?.type === ContractType.Prepaid && <PrepaidContractInfo primaryContract={primaryContract} />}
    </Wrapper>
  )
}
